<script setup lang="ts">
/**
 * Отбор в панели проекта — макет 34:249: чипы и поиск по ⌘K. Общий у доски
 * и списка: это одна и та же полоса в одном и том же месте экрана.
 *
 * Поиск отправляется не на каждую букву: за клавишами доска не успевает, а
 * каждый промежуточный запрос — полный обход колонок на бэкенде. Полсекунды
 * тишины — и запрос уходит.
 *
 * Метки в отборе бэкенд принимает, но чипа для них нет: их нет ни в макете
 * панели, ни на карточке, ни в строке списка — фильтр по тому, чего не видно,
 * отбирает вслепую. Появится вместе с метками в интерфейсе (DEV-1501).
 */
import { useDebounceFn, useEventListener } from '@vueuse/core'
import { Search } from 'lucide-vue-next'
import { computed, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'

import FilterChip from '@/components/FilterChip.vue'
import { PRIORITIES, typesOfProfile } from '@/features/issues/api'
import type { IssueFilters } from '@/features/issues/filters'
import { listMembers, type ProjectMember } from '@/features/projects/api'

import type { V1IssueType, V1Priority } from '@/api/clients/tracker-issue'

/** Сколько ждать тишины в поиске, прежде чем спрашивать сервер */
const SEARCH_DEBOUNCE = 400

const props = defineProps<{
  filters: IssueFilters
  projectId: string
  /** Профиль проекта решает, какие типы задач в нём вообще бывают */
  typeProfile: string
}>()

const emit = defineEmits<{ change: [filters: IssueFilters] }>()

const { t } = useI18n()

const members = ref<ProjectMember[]>([])

// Один список на оба чипа: и исполнитель, и автор выбираются из участников
// проекта. Автор, ушедший из проекта, в списке не появится — его задачи
// остаются видны, просто отобрать их по нему уже нельзя.
const people = computed(() =>
  members.value
    .filter((member) => member.name)
    .map((member) => ({ value: member.id, label: member.name })),
)

const types = computed(() =>
  typesOfProfile(props.typeProfile).map((value) => ({
    value,
    label: t(`issues.types.${value}`),
  })),
)

const priorities = computed(() =>
  PRIORITIES.map((value) => ({ value, label: t(`issues.priorities.${value}`) })),
)

/** Строка в поле живёт отдельно от отбора: она меняется быстрее запросов */
const query = ref(props.filters.search)

const searchInput = ref<HTMLInputElement | null>(null)

const isMac = typeof navigator !== 'undefined' && /Mac|iPhone|iPad/.test(navigator.userAgent)
const shortcut = isMac ? '⌘K' : 'Ctrl K'

const change = (patch: Partial<IssueFilters>) => emit('change', { ...props.filters, ...patch })

const pushQuery = useDebounceFn(() => change({ search: query.value }), SEARCH_DEBOUNCE)

// Сброс отбора снаружи (смена проекта) должен опустошить и поле.
watch(
  () => props.filters.search,
  (search) => {
    if (search !== query.value) query.value = search
  },
)

useEventListener(document, 'keydown', (event: KeyboardEvent) => {
  if (event.key !== 'k' || !(event.metaKey || event.ctrlKey)) return

  event.preventDefault()
  searchInput.value?.focus()
})

// Участники нужны чипам исполнителя и автора; отказ здесь оставляет их
// пустыми и не мешает остальным фильтрам.
watch(
  () => props.projectId,
  async (projectId) => {
    members.value = projectId ? await listMembers(projectId).catch(() => []) : []
  },
  { immediate: true },
)
</script>

<template>
  <FilterChip
    :label="t('toolbar.filters.assignee')"
    :options="people"
    :model-value="filters.assigneeIds"
    :empty-text="t('issues.create.noMembers')"
    @update:model-value="change({ assigneeIds: $event })"
  />

  <FilterChip
    :label="t('toolbar.filters.reporter')"
    :options="people"
    :model-value="filters.reporterIds"
    :empty-text="t('issues.create.noMembers')"
    @update:model-value="change({ reporterIds: $event })"
  />

  <FilterChip
    :label="t('toolbar.filters.type')"
    :options="types"
    :model-value="filters.types"
    :empty-text="t('toolbar.filters.empty')"
    @update:model-value="change({ types: $event as V1IssueType[] })"
  />

  <FilterChip
    :label="t('toolbar.filters.priority')"
    :options="priorities"
    :model-value="filters.priorities"
    :empty-text="t('toolbar.filters.empty')"
    @update:model-value="change({ priorities: $event as V1Priority[] })"
  />

  <label class="bsearch">
    <Search class="bsearch__icon" :size="14" />
    <input
      ref="searchInput"
      v-model="query"
      class="bsearch__input"
      type="search"
      :placeholder="t('toolbar.search')"
      :aria-label="t('toolbar.search')"
      @input="pushQuery"
      @keydown.enter.prevent="change({ search: query })"
    />
    <kbd class="bsearch__kbd">{{ shortcut }}</kbd>
  </label>
</template>

<style scoped>
.bsearch {
  display: flex;
  align-items: center;
  gap: 8px;
  /* Ширину панель занимает отсюда: чипов в ней четыре, и на ноутбуке они
     вместе с кнопкой «Создать» упираются в край экрана. Уступает место поле —
     чипы держат подписи целиком, потому что обрезанный «Прио…» не отвечает на
     вопрос, ради которого чип в панели. Сто двадцать пикселей поля — это
     иконка, подсказка ⌘K и место под слово; меньше уже не поле. */
  flex: 0 1 220px;
  min-width: 120px;
  height: 32px;
  padding: 0 10px;
  border: 1px solid var(--color-border);
  border-radius: var(--radius-sm);
  background: var(--color-surface);
}

.bsearch:focus-within {
  border-color: var(--color-primary);
}

.bsearch__icon {
  flex-shrink: 0;
  color: var(--color-text-muted);
}

.bsearch__input {
  flex: 1;
  min-width: 0;
  border: none;
  background: none;
  color: var(--color-text-primary);
  font-family: var(--font-family-sans);
  font-size: var(--font-size-body-sm);
  outline: none;
}

.bsearch__input::placeholder {
  color: var(--color-text-muted);
}

/* Крестик очистки у type=search рисуют по-своему во всех браузерах и ни в
   одном — как в макете. Поле чистится тем же способом, что и любое другое. */
.bsearch__input::-webkit-search-decoration,
.bsearch__input::-webkit-search-cancel-button {
  appearance: none;
}

.bsearch__kbd {
  display: inline-flex;
  align-items: center;
  flex-shrink: 0;
  height: 20px;
  padding: 0 5px;
  border-radius: 5px;
  background: var(--color-bg-muted);
  color: var(--color-text-muted);
  font-family: var(--font-family-mono);
  font-size: var(--font-size-overline);
}
</style>
